import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useProfilingProgressContext } from 'src/providers/ProfilingProgressProvider/ProfilingProgress.context';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { useWapiNeverConnected } from 'src/hooks/whatsapp/useWapiNeverConnected.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import {
  useWhatsAppConnectState,
  type tWapiConnectAnalytics,
  type tWapiDevScenario,
} from 'src/hooks/whatsapp/useWhatsAppConnectState.hook';
import {
  readWapiFailureDetails,
  toObservedActivation,
  WAPI_DEV_SCENARIO_CLOSED_STEP,
  type tConnectionState,
  type tPostConnectionPhase,
} from 'src/hooks/whatsapp/whatsappConnect.types';
import { getEmbeddedSignupAttemptId } from 'src/lib/facebook/embeddedSignupTelemetry';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import { WIZARD_CONNECTION_FLOW } from './WhatsAppConnectionWizard.constants';
import type {
  tWizardActivatingStage,
  tWizardAdvisorCtaClick,
  tWizardHistorySync,
} from './WhatsAppConnectionWizard.types';
import {
  WhatsAppConnectionWizardContext,
  type tWhatsAppConnectionWizardContext,
} from './WhatsAppConnectionWizard.context';
import {
  isExchangeErrorWithoutVerdict,
  isWindowFullScreen,
  resolveConnectionMoment,
  toCloseStepKey,
} from './WhatsAppConnectionWizard.functions';

type tWhatsAppConnectionWizardProviderProps = PropsWithChildren<{
  /**
   * False while the wizard's flag is off or not loaded: no wizard state and a
   * null context. ModalsProvider always passes it; omitted, the wizard is on.
   */
  isEnabled?: boolean;
  /**
   * False while ModalsProvider shows the current modal in the wizard's place,
   * so its opens are not counted as wizard opens. The state and the context
   * stay as they are. Omitted, the wizard is the modal shown.
   */
  isWizardShown?: boolean;
  /** Called with the workspace id when a real connection reaches connected. */
  onConnected?: (workspaceId: string) => void;
  /** Called when the person leaves the wizard for the current modal. */
  onLegacyChosen?: () => void;
}>;

type tWhatsAppConnectionWizardStateProps = {
  workspaceId: string;
  onValueChange: (value: tPublishedWizardState | null) => void;
  /**
   * True the first time it is called during an opening of the modal, false
   * after that until the modal closes.
   */
  claimOpening: () => boolean;
  onConnected?: (workspaceId: string) => void;
  onLegacyChosen?: () => void;
};

/** The wizard state of one workspace, as the state holder publishes it. */
type tPublishedWizardState = {
  workspaceId: string;
  value: tWhatsAppConnectionWizardContext;
};

/**
 * The closes a simulated scene shows as already happened before its own, so
 * the repeated-close screen opens in one click.
 */
const SIMULATED_EARLIER_CLOSE_KEYS: Partial<
  Record<tWapiDevScenario, string[]>
> = {
  closed_again_at_step: [toCloseStepKey(WAPI_DEV_SCENARIO_CLOSED_STEP)],
};

/** What a simulated activation scene shows beyond its reducer state. */
type tSimulatedActivationDetails = {
  activatingStage?: tWizardActivatingStage;
  minutesSinceActivationDecided?: number;
  /** Activation failures counted before the scene's own. */
  earlierActivationFailures?: number;
  /** Exchange errors with no verdict counted before the scene's own. */
  earlierExchangeErrors?: number;
};

const SIMULATED_ACTIVATION_DETAILS: Partial<
  Record<tWapiDevScenario, tSimulatedActivationDetails>
> = {
  activating_over_3_seconds: { activatingStage: 'overThreeSeconds' },
  activating_over_30_seconds: { activatingStage: 'overThirtySeconds' },
  activation_pending_over_an_hour: { minutesSinceActivationDecided: 61 },
  activation_failed_again: { earlierActivationFailures: 1 },
  fail_error_again: { earlierExchangeErrors: 1 },
};

/** The history sync a simulated connected scene shows. */
const SIMULATED_HISTORY_SYNC: Partial<
  Record<tWapiDevScenario, tWizardHistorySync>
> = {
  success: 'syncing',
  success_without_history: 'notShared',
};

/** The number simulated screens show in "Tu conexión". */
const SIMULATED_PHONE_NUMBER = '+52 55 1234 5678';

/** What a simulated scene publishes in place of the integration row. */
type tSimulatedPresentation = {
  activatingStage?: tWizardActivatingStage;
  activationDecidedAt?: string;
  historySync?: tWizardHistorySync;
};

// The connected screen stays until the person leaves it, so the countdown
// the shared hook runs on success has nothing to do here.
const ignoreSuccessCountdown = () => undefined;

/** The failure phases a reload restores from storage. */
function isRestorableFailure(phase: tPostConnectionPhase): boolean {
  return phase === 'failed_error' || phase === 'failed_closed';
}

/** Whether the state holds an exchange error that carries no verdict. */
function isRestoredExchangeErrorWithoutVerdict(
  state: tConnectionState,
): boolean {
  return (
    state.phase === 'failed_error' &&
    isExchangeErrorWithoutVerdict(state.errorCode)
  );
}

/**
 * Holds the wizard's connection for one workspace: the shared connection
 * state machine, the chosen path and the consecutive closes of Meta's window.
 * It outlives the modal, so a connection keeps going, and Meta's code is still
 * exchanged, after the person closes the wizard.
 *
 * It renders nothing and publishes its value through `onValueChange`, so the
 * page below the provider never moves in the tree when this mounts.
 */
function WhatsAppConnectionWizardState(
  props: tWhatsAppConnectionWizardStateProps,
) {
  const {
    workspaceId,
    onValueChange,
    claimOpening,
    onConnected,
    onLegacyChosen,
  } = props;
  const {
    whatsAppBusinessConnectionModalIsOpen,
    whatsAppBusinessConnectionModalSource,
    setWhatsAppBusinessConnectionModalIsOpen,
  } = useModalsContext();
  const { trackWapiEvent: trackWapiEventOfRender } = useWapiAnalytics();
  const { refetchIntegrations, whatsappBusinessIntegration } =
    useIntegrationsContext();
  const isWorkspaceNeverConnected = useWapiNeverConnected();
  // Read through a latest-value ref so the success effect does not depend on
  // it and the re-check runs once per success.
  const { recheckSyncStatus } = useProfilingProgressContext();
  const recheckSyncStatusRef = useRef(recheckSyncStatus);
  recheckSyncStatusRef.current = recheckSyncStatus;
  // `trackWapiEvent` is a new function on every render. Read through a
  // latest-value ref, it keeps the callbacks below stable, so the published
  // value changes only when the state does; otherwise each publish would
  // re-render this holder into another publish.
  const trackWapiEventRef = useRef(trackWapiEventOfRender);
  trackWapiEventRef.current = trackWapiEventOfRender;
  const trackWapiEvent = useCallback<typeof trackWapiEventOfRender>(
    (name, payload) =>
      trackWapiEventRef.current(name, {
        ...payload,
        connection_flow: WIZARD_CONNECTION_FLOW,
      }),
    [],
  );

  const closeModal = useCallback(() => {
    setWhatsAppBusinessConnectionModalIsOpen(false);
  }, [setWhatsAppBusinessConnectionModalIsOpen]);

  // The path of the attempt lives in the connection state, which the hook
  // below returns, so the callbacks read it through a ref assigned after it.
  const attemptPathRef = useRef<tEmbeddedSignupPath | undefined>(undefined);

  // A stable analytics object keeps `launchConnection` stable across renders,
  // since the shared hook lists it in its callbacks' dependencies. An attempt
  // without a path sends no `path` key rather than a placeholder value.
  const wapiAnalytics = useMemo<tWapiConnectAnalytics>(() => {
    const readAttemptPath = () =>
      attemptPathRef.current ? { path: attemptPathRef.current } : {};
    return {
      onStarted: (path) =>
        trackWapiEvent(
          WAPI_EVENTS.INTEGRATION_CONNECTION_STARTED,
          path ? { path } : {},
        ),
      onSuccess: () =>
        trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_SUCCESS, {
          status: 'success',
          ...readAttemptPath(),
        }),
      onError: (reason, error) =>
        trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_ERROR, {
          status: 'error',
          reason,
          errorMessage: error?.message,
          signup_attempt_id: getEmbeddedSignupAttemptId(),
          ...readWapiFailureDetails(error),
          ...readAttemptPath(),
        }),
    };
  }, [trackWapiEvent]);

  const {
    state,
    launchConnection,
    retry,
    reset,
    backToConnect,
    __devSimulate,
  } = useWhatsAppConnectState({
    workspaceId,
    onSuccess: ignoreSuccessCountdown,
    // The person opened the modal on purpose, so an integration that is
    // already connected must not start the success countdown and close it.
    autoSuccessOnAlreadyConnected: false,
    analytics: wapiAnalytics,
    // The wizard opened from onboarding is its own surface, so its attempts
    // stay apart from the onboarding connect screen's and the dashboard's.
    surface:
      whatsAppBusinessConnectionModalSource === 'onboarding'
        ? 'onboarding_wizard'
        : 'dashboard_modal',
  });
  attemptPathRef.current = state.path;

  // Read through a latest-value ref like `trackWapiEvent`, so the published
  // value never depends on the helper's identity.
  const devSimulateRef = useRef(__devSimulate);
  devSimulateRef.current = __devSimulate;

  // What a person does on a simulated screen is a preview, not product usage.
  // The connection callbacks above keep the unguarded tracker: a real attempt
  // started from a simulated screen reports its start before this render's
  // state has left the simulation.
  const isSimulated = state.isSimulated;
  const trackScreenEvent = useCallback<typeof trackWapiEvent>(
    (...eventArguments) => {
      if (!isSimulated) trackWapiEvent(...eventArguments);
    },
    [isSimulated, trackWapiEvent],
  );

  // This holder mounts on the first open in the workspace, so its mount counts
  // as an open, like every later reopen. The provider's claim, not the
  // dependencies or this holder's lifetime, decides, so neither a re-render
  // nor a remount while open counts the opening again.
  useEffect(() => {
    if (!whatsAppBusinessConnectionModalIsOpen || !claimOpening()) return;
    trackScreenEvent(
      WAPI_EVENTS.INTEGRATION_CONNECTION_OPENED,
      whatsAppBusinessConnectionModalSource
        ? { source: whatsAppBusinessConnectionModalSource }
        : {},
    );
  }, [
    whatsAppBusinessConnectionModalIsOpen,
    whatsAppBusinessConnectionModalSource,
    trackScreenEvent,
    claimOpening,
  ]);

  // Seeded from the path saved with a restored attempt, so a retry after a
  // reload opens the flow the person chose. Otherwise no answer is selected:
  // the person picks the path on purpose.
  const [selectedPath, setSelectedPath] = useState<tEmbeddedSignupPath | null>(
    () => state.path ?? null,
  );
  // True once the person clicked a path, so a close can tell a choice apart
  // from a restored path the person never touched.
  const [pathWasChosen, setPathWasChosen] = useState(false);
  const selectPath = useCallback((path: tEmbeddedSignupPath | null) => {
    setSelectedPath(path);
    setPathWasChosen(path !== null);
  }, []);

  // A failure this holder started with came from storage, not from this
  // session. It stays marked until the phase leaves it, so a retry that fails
  // live is never shown as a past attempt. Updated during render, like the
  // close history below, so no render shows the mark on a live attempt.
  const [restoredFailureAt, setRestoredFailureAt] = useState<number | null>(
    () => (isRestorableFailure(state.phase) ? state.lastAttemptAt : null),
  );
  if (restoredFailureAt !== null && !isRestorableFailure(state.phase)) {
    setRestoredFailureAt(null);
  }

  // On success the integrations endpoint is asked once, so the rest of the
  // dashboard sees the connection even when the workspace socket arrives after
  // the SDK callback, and the sync status once, since its startup poll may
  // have run out before the connection created the sync. The ref keeps it to
  // one request each per success phase. A simulated success connected
  // nothing, so it asks for nothing.
  const successRefetchedRef = useRef(false);
  // When the person last started or retried an attempt; null when this
  // session launched none. The re-check tells the attempt's own sync row from
  // an earlier connection's by it, so it is the launch, not the success, that
  // counts: Meta can hold a number for hours.
  const attemptLaunchedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (state.phase !== 'success') {
      successRefetchedRef.current = false;
      return;
    }
    if (successRefetchedRef.current || isSimulated) return;
    successRefetchedRef.current = true;
    void refetchIntegrations();
    // With no launch to compare against, only a row from about now counts.
    recheckSyncStatusRef.current(attemptLaunchedAtRef.current ?? Date.now());
  }, [state.phase, isSimulated, refetchIntegrations]);

  // Read when the attempt launches: once connected, the integration row no
  // longer says whether this was the workspace's first connection.
  const [wasNeverConnectedAtLaunch, setWasNeverConnectedAtLaunch] =
    useState(false);

  // `success` has no way back in the reducer, and this state outlives the
  // modal, so a reopen after any close of a finished connection starts a new
  // one, also when it finished while the modal was closed. Only the reopen
  // resets, so the exit never shows the start screen. A layout effect so the
  // finished connection never paints on reopen.
  const isFinishedConnection = state.phase === 'success';
  const wasModalOpenRef = useRef(whatsAppBusinessConnectionModalIsOpen);
  useLayoutEffect(() => {
    const isReopen =
      whatsAppBusinessConnectionModalIsOpen && !wasModalOpenRef.current;
    wasModalOpenRef.current = whatsAppBusinessConnectionModalIsOpen;
    if (isReopen && isFinishedConnection) {
      reset();
      setWasNeverConnectedAtLaunch(false);
      attemptLaunchedAtRef.current = null;
    }
  }, [whatsAppBusinessConnectionModalIsOpen, isFinishedConnection, reset]);

  // The reducer never lets an absent row end a hold, since that row can be a
  // read that has not caught up with the connect. Once this pending phase has
  // seen the row held, an absent row means the held connection is gone, and
  // pending has no action that leaves it, so the wizard starts over. Tracked
  // during render, so the mark never outlives the phase that set it.
  const observedActivation = toObservedActivation(
    whatsappBusinessIntegration?.state,
  );
  const isRealVerifying = state.phase === 'verifying' && !isSimulated;
  const [isHoldSeen, setIsHoldSeen] = useState(
    isRealVerifying && observedActivation === 'held',
  );
  if (isHoldSeen && !isRealVerifying) {
    setIsHoldSeen(false);
  } else if (!isHoldSeen && isRealVerifying && observedActivation === 'held') {
    setIsHoldSeen(true);
  }
  const isHeldConnectionGone = isHoldSeen && observedActivation === 'absent';
  useLayoutEffect(() => {
    if (isHeldConnectionGone) reset();
  }, [isHeldConnectionGone, reset]);

  // Updated during render, on the phase change itself: the next retry clears
  // Meta's step, and a moment derived after an effect would first render the
  // close without its repeat. Every streak counts attempt ends the same way.
  const [attemptHistory, setAttemptHistory] = useState<{
    phase: tPostConnectionPhase;
    closedStepKeys: string[];
    activationFailureCount: number;
    exchangeErrorCount: number;
  }>({
    phase: state.phase,
    closedStepKeys: [],
    activationFailureCount: 0,
    exchangeErrorCount: isRestoredExchangeErrorWithoutVerdict(state) ? 1 : 0,
  });
  let { closedStepKeys, activationFailureCount, exchangeErrorCount } =
    attemptHistory;
  if (attemptHistory.phase !== state.phase) {
    if (
      attemptHistory.phase === 'connecting' ||
      attemptHistory.phase === 'verifying'
    ) {
      // A held activation has no final verdict, so its failure count survives
      // until the attempt succeeds or fails. Other outcomes break the streak.
      closedStepKeys =
        state.phase === 'failed_closed'
          ? [...closedStepKeys, toCloseStepKey(state.signupStep)]
          : [];
      activationFailureCount =
        state.phase === 'activation_failed'
          ? activationFailureCount + 1
          : state.phase === 'verifying'
            ? activationFailureCount
            : 0;
      exchangeErrorCount =
        state.phase === 'failed_error' &&
        isExchangeErrorWithoutVerdict(state.errorCode)
          ? exchangeErrorCount + 1
          : 0;
    } else if (state.phase === 'success') {
      activationFailureCount = 0;
    } else if (state.phase === 'activation_failed') {
      // A failure the integration row reports, on open or after a hold,
      // counts as one, so the next live failure is the second.
      activationFailureCount = Math.max(activationFailureCount, 1);
    } else if (isRestoredExchangeErrorWithoutVerdict(state)) {
      // A no-verdict error restored after a reload counts as one, like an
      // activation failure the integration row reports.
      exchangeErrorCount = Math.max(exchangeErrorCount, 1);
    }
    setAttemptHistory({
      phase: state.phase,
      closedStepKeys,
      activationFailureCount,
      exchangeErrorCount,
    });
  }

  const moment = useMemo(
    () => resolveConnectionMoment({ state, closedStepKeys }),
    [state, closedStepKeys],
  );

  // A simulated success connected nothing, so only a real one is reported.
  // Read through a latest-value ref, so a new callback never reports again.
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const isConnected =
    state.phase === 'success' && moment.kind === 'connected' && !isSimulated;
  useEffect(() => {
    if (isConnected) onConnectedRef.current?.(workspaceId);
  }, [isConnected, workspaceId]);

  const [simulatedPresentation, setSimulatedPresentation] =
    useState<tSimulatedPresentation>({});

  const simulate = useCallback((scene: tWapiDevScenario) => {
    if (!devSimulateRef.current(scene)) return;
    const details = SIMULATED_ACTIVATION_DETAILS[scene] ?? {};
    // A scene's actions render once, so the attempt history above never sees
    // its attempt open. It starts each scene's attempt from an open window,
    // after the closes and failures the scene's screen counts.
    setAttemptHistory({
      phase: 'connecting',
      closedStepKeys: SIMULATED_EARLIER_CLOSE_KEYS[scene] ?? [],
      activationFailureCount: details.earlierActivationFailures ?? 0,
      exchangeErrorCount: details.earlierExchangeErrors ?? 0,
    });
    setSimulatedPresentation({
      activatingStage: details.activatingStage,
      historySync: SIMULATED_HISTORY_SYNC[scene],
      activationDecidedAt:
        details.minutesSinceActivationDecided === undefined
          ? undefined
          : new Date(
              Date.now() - details.minutesSinceActivationDecided * 60_000,
            ).toISOString(),
    });
  }, []);

  // A simulated screen shows its own sample data, never the real row's.
  const phoneNumber = isSimulated
    ? SIMULATED_PHONE_NUMBER
    : whatsappBusinessIntegration?.phoneNumber;
  const activationDecidedAt = isSimulated
    ? simulatedPresentation.activationDecidedAt
    : whatsappBusinessIntegration?.activationDecidedAt;
  const simulatedActivatingStage = isSimulated
    ? simulatedPresentation.activatingStage
    : undefined;
  const simulatedHistorySync = isSimulated
    ? simulatedPresentation.historySync
    : undefined;
  // A simulated connection always shows the moment, so it can be reviewed.
  const playsBrandMoment = isSimulated || wasNeverConnectedAtLaunch;

  // Read at the click that opens Meta: the waiting screen tells where Meta
  // opened even after the window changes size.
  const [isMetaInAnotherTab, setIsMetaInAnotherTab] = useState(false);

  // With no answer Meta stays closed: the shared login options would open it
  // on Coexistencia without anyone choosing it. The wizard asks for the
  // answer instead.
  const handleConnect = useCallback(() => {
    if (!selectedPath) return;
    attemptLaunchedAtRef.current = Date.now();
    setWasNeverConnectedAtLaunch(isWorkspaceNeverConnected);
    setIsMetaInAnotherTab(isWindowFullScreen());
    void launchConnection({ path: selectedPath, pinPopupToLeftEdge: true });
  }, [isWorkspaceNeverConnected, launchConnection, selectedPath]);

  // A retry opens Meta on the path the person chose, else on the failed
  // attempt's own path. A failure with neither cannot tell which flow to
  // reopen, since the activating phases are not persisted, so the person
  // chooses it again.
  const isRetryWithoutPath = !selectedPath && !state.path;
  const handleRetry = useCallback(() => {
    trackScreenEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_RETRY_CLICKED, {
      retry_count: state.retryCount,
    });
    if (isRetryWithoutPath) {
      backToConnect();
      return;
    }
    attemptLaunchedAtRef.current = Date.now();
    setWasNeverConnectedAtLaunch(isWorkspaceNeverConnected);
    setIsMetaInAnotherTab(isWindowFullScreen());
    void retry({
      path: selectedPath ?? state.path,
      pinPopupToLeftEdge: true,
    });
  }, [
    backToConnect,
    isRetryWithoutPath,
    isWorkspaceNeverConnected,
    retry,
    selectedPath,
    state.path,
    state.retryCount,
    trackScreenEvent,
  ]);

  const handleEscalatedMount = useCallback(() => {
    trackScreenEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_ESCALATED_VIEWED);
  }, [trackScreenEvent]);

  const handleAdvisorCtaClick = useCallback(
    (click: tWizardAdvisorCtaClick) => {
      trackScreenEvent(
        click.kind === 'live'
          ? WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_LIVE_CTA_CLICKED
          : WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_SCHEDULE_CTA_CLICKED,
        { location: click.location },
      );
    },
    [trackScreenEvent],
  );

  // No close is reported: the modal stays open and the attempt goes on in the
  // current modal, so a switch never counts as an abandon.
  const onLegacyChosenRef = useRef(onLegacyChosen);
  onLegacyChosenRef.current = onLegacyChosen;
  const handleConnectWithoutGuide = useCallback(() => {
    trackScreenEvent(
      WAPI_EVENTS.INTEGRATION_CONNECTION_CONNECT_WITHOUT_GUIDE_CLICKED,
      {
        moment: moment.kind,
        ...(whatsAppBusinessConnectionModalSource
          ? { source: whatsAppBusinessConnectionModalSource }
          : {}),
        ...(state.path ? { path: state.path } : {}),
      },
    );
    onLegacyChosenRef.current?.();
  }, [
    moment.kind,
    state.path,
    trackScreenEvent,
    whatsAppBusinessConnectionModalSource,
  ]);

  const handleClose = useCallback(() => {
    // `path` is the attempt's path; `selected_path` is the current answer,
    // which a close before any attempt still reports. With no answer the key
    // is left out rather than filled.
    trackScreenEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_CLOSED_BY_USER, {
      phase: state.phase,
      ...(state.path ? { path: state.path } : {}),
      ...(selectedPath ? { selected_path: selectedPath } : {}),
      path_was_chosen: pathWasChosen,
    });
    closeModal();
  }, [
    closeModal,
    pathWasChosen,
    selectedPath,
    state.path,
    state.phase,
    trackScreenEvent,
  ]);

  const value = useMemo<tWhatsAppConnectionWizardContext>(
    () => ({
      state,
      moment,
      selectedPath,
      selectPath,
      handleClose,
      handleConnect,
      handleRetry,
      handleEscalatedMount,
      handleAdvisorCtaClick,
      backToConnect,
      handleConnectWithoutGuide,
      isMetaInAnotherTab,
      simulate,
      ...(restoredFailureAt !== null ? { restoredFailureAt } : {}),
      activationFailureCount,
      exchangeErrorCount,
      playsBrandMoment,
      ...(phoneNumber ? { phoneNumber } : {}),
      ...(activationDecidedAt ? { activationDecidedAt } : {}),
      ...(simulatedActivatingStage ? { simulatedActivatingStage } : {}),
      ...(simulatedHistorySync ? { simulatedHistorySync } : {}),
    }),
    [
      activationFailureCount,
      exchangeErrorCount,
      playsBrandMoment,
      simulatedHistorySync,
      phoneNumber,
      activationDecidedAt,
      simulatedActivatingStage,
      state,
      moment,
      selectedPath,
      selectPath,
      handleClose,
      handleConnect,
      handleRetry,
      handleEscalatedMount,
      handleAdvisorCtaClick,
      backToConnect,
      handleConnectWithoutGuide,
      isMetaInAnotherTab,
      simulate,
      restoredFailureAt,
    ],
  );

  // A layout effect, so the page and the modal read the new value before the
  // browser paints. Unmounting withdraws it, so a later holder for another
  // workspace never starts from this one's state.
  useLayoutEffect(() => {
    onValueChange({ workspaceId, value });
  }, [onValueChange, workspaceId, value]);
  useLayoutEffect(() => () => onValueChange(null), [onValueChange]);

  return null;
}

/**
 * Keeps the WhatsApp API connection wizard's state alive while its modal is
 * closed, and shares it with the rest of the page, such as the integrations
 * card. ModalsProvider always mounts it around the modals and the page and
 * enables it once `integrations_wapi-connection-wizard` is loaded and on.
 * Disabled, it publishes a null context and never touches the wizard's state
 * or storage, so the flags settling changes no element above the page.
 *
 * The shared connection hook mounts only after the modal first opens in the
 * current workspace, so it never runs beside onboarding's instance for a person
 * who never opened the wizard. It is keyed by the workspace id, so its storage
 * hydration reads the right workspace and a workspace switch starts a fresh
 * connection; returning to a workspace needs a new open. Until then the
 * children render with a null context.
 *
 * @param {tWhatsAppConnectionWizardProviderProps} props - The modals and the
 * page, and whether the wizard is enabled.
 * @returns {JSX.Element} The provider around its children.
 */
export function WhatsAppConnectionWizardProvider(
  props: tWhatsAppConnectionWizardProviderProps,
) {
  const {
    children,
    isEnabled = true,
    isWizardShown = true,
    onConnected,
    onLegacyChosen,
  } = props;
  const modals = useModalsContext();
  // While disabled, or while the current modal shows in the wizard's place,
  // the old modal is the one that opens, so its opens never count as a wizard
  // open.
  const whatsAppBusinessConnectionModalIsOpen =
    isEnabled && isWizardShown && modals.whatsAppBusinessConnectionModalIsOpen;
  const { currentWorkspaceId } = useWorkspaceContext();
  const [openedWorkspaceId, setOpenedWorkspaceId] = useState(
    whatsAppBusinessConnectionModalIsOpen ? currentWorkspaceId : undefined,
  );
  // Leaving for another workspace forgets the open, so coming back with the
  // modal closed does not mount the shared hook again. A workspace id that is
  // unknown for a moment (a user refetch) keeps it, so a connection in flight
  // resumes when the id returns.
  if (
    currentWorkspaceId &&
    openedWorkspaceId !== currentWorkspaceId &&
    (whatsAppBusinessConnectionModalIsOpen || openedWorkspaceId)
  ) {
    setOpenedWorkspaceId(
      whatsAppBusinessConnectionModalIsOpen ? currentWorkspaceId : undefined,
    );
  }

  // The holder unmounts while the workspace id is unknown for a moment, so
  // whether the current opening was already counted lives here, and is
  // cleared on a close even while no holder is mounted. The holder reads the
  // modal's open from the modals context, so an opening of the current modal
  // is refused here.
  const isWizardShownRef = useRef(isWizardShown);
  isWizardShownRef.current = isWizardShown;
  const isOpeningClaimedRef = useRef(false);
  const claimOpening = useCallback(() => {
    if (!isWizardShownRef.current || isOpeningClaimedRef.current) return false;
    isOpeningClaimedRef.current = true;
    return true;
  }, []);
  useEffect(() => {
    if (!whatsAppBusinessConnectionModalIsOpen) {
      isOpeningClaimedRef.current = false;
    }
  }, [whatsAppBusinessConnectionModalIsOpen]);

  const [publishedState, setPublishedState] =
    useState<tPublishedWizardState | null>(null);
  const isStateMounted =
    isEnabled &&
    Boolean(currentWorkspaceId) &&
    openedWorkspaceId === currentWorkspaceId;
  const value =
    isStateMounted && publishedState?.workspaceId === currentWorkspaceId
      ? (publishedState?.value ?? null)
      : null;

  // The state holder is a sibling of the children, never their parent, so its
  // mount on the first open and its remount on a workspace switch leave the
  // page where it is instead of remounting it.
  return (
    <WhatsAppConnectionWizardContext.Provider value={value}>
      {isStateMounted && currentWorkspaceId ? (
        <WhatsAppConnectionWizardState
          key={currentWorkspaceId}
          workspaceId={currentWorkspaceId}
          onValueChange={setPublishedState}
          claimOpening={claimOpening}
          onConnected={onConnected}
          onLegacyChosen={onLegacyChosen}
        />
      ) : null}
      {children}
    </WhatsAppConnectionWizardContext.Provider>
  );
}
